import type { TFunction } from "i18next";

import type { EventState } from "../types/event";

/**
 * Event state → visual presentation.
 *
 * Mirrors `api/mappings/event_colors.py` on the backend. Each preset
 * gives us:
 *   - `labelKey` — translation key for the badge text; the state itself is a
 *                  wire value, so only the display is translated (see
 *                  {@link eventStateLabel})
 *   - `classes` — tailwind classes for badge background + text
 *   - `hex`    — raw color, used anywhere tailwind can't reach (inline
 *                styles on SVG pins, chart series, etc.)
 *
 * Kept in this module (not BE-provided) because it's a pure rendering
 * concern and we want the badge to render even if we fetch the event
 * lazily — we already know the state from the dump event response.
 */
type EventStateLabelKey =
  | "eventStates.dump"
  | "eventStates.dig"
  | "eventStates.swingEmpty"
  | "eventStates.swingLoaded"
  | "eventStates.dumpNoTruck"
  | "eventStates.boulderHandling"
  | "eventStates.truckEmpty"
  | "eventStates.truckLoaded"
  | "eventStates.abnormal"
  | "eventStates.productive"
  | "eventStates.unproductive"
  | "eventStates.truckCycle"
  | "eventStates.fallback";

interface EventPresentation {
  labelKey: EventStateLabelKey;
  classes: string;
  hex: string;
}

const PRESENTATION: Record<EventState, EventPresentation> = {
  dump: {
    labelKey: "eventStates.dump",
    classes: "bg-red-600 text-white",
    hex: "#DC2626",
  },
  dig: {
    labelKey: "eventStates.dig",
    classes: "bg-purple-600 text-white",
    hex: "#9333EA",
  },
  swing_empty: {
    labelKey: "eventStates.swingEmpty",
    classes: "bg-blue-600 text-white",
    hex: "#2563EB",
  },
  swing_loaded: {
    labelKey: "eventStates.swingLoaded",
    classes: "bg-green-600 text-white",
    hex: "#16A34A",
  },
  dump_no_truck: {
    labelKey: "eventStates.dumpNoTruck",
    classes: "bg-orange-500 text-white",
    hex: "#F97316",
  },
  boulder_handling: {
    labelKey: "eventStates.boulderHandling",
    classes: "bg-black text-white",
    hex: "#000000",
  },
  truck_empty: {
    labelKey: "eventStates.truckEmpty",
    classes: "bg-amber-800 text-white",
    hex: "#92400E",
  },
  truck_loaded: {
    labelKey: "eventStates.truckLoaded",
    classes: "bg-pink-500 text-white",
    hex: "#EC4899",
  },
  abnormal: {
    labelKey: "eventStates.abnormal",
    classes: "bg-fuchsia-600 text-white",
    hex: "#C026D3",
  },
  productive: {
    labelKey: "eventStates.productive",
    classes: "bg-green-600 text-white",
    hex: "#16A34A",
  },
  unproductive: {
    labelKey: "eventStates.unproductive",
    classes: "bg-red-600 text-white",
    hex: "#DC2626",
  },
  truck_cycle: {
    labelKey: "eventStates.truckCycle",
    classes: "bg-cyan-600 text-white",
    hex: "#0891B2",
  },
};

const FALLBACK: EventPresentation = {
  labelKey: "eventStates.fallback",
  classes: "bg-muted text-foreground",
  hex: "#6B7280",
};

/**
 * Map an event state string to its badge presentation.
 *
 * The BE emits both snake_case (`dump_no_truck`) and kebab-case
 * (`dump-no-truck`) variants depending on which endpoint you hit,
 * so we normalise both before lookup. The backend also supplies a
 * hex colour now via `event.color` — callers should prefer that
 * over `hex` from this util; the hex here is only a fallback for
 * the (rare) case where no event payload is available.
 */
export const getEventPresentation = (state: string | null | undefined): EventPresentation => {
  if (!state) return FALLBACK;
  const normalised = state.replace(/-/g, "_") as EventState;
  return PRESENTATION[normalised] ?? FALLBACK;
};

/**
 * Badge text for an event state. A state this map doesn't know about renders
 * as its own uppercased value rather than the generic fallback, so a new
 * backend state stays legible instead of collapsing to "EVENT".
 */
export const eventStateLabel = (
  state: string | null | undefined,
  t: TFunction<"mlExports">,
): string => {
  if (!state) return t("eventStates.fallback");
  const normalised = state.replace(/-/g, "_") as EventState;
  const preset = PRESENTATION[normalised];
  return preset ? t(preset.labelKey) : state.replace(/[-_]/g, " ").toUpperCase();
};
